import { createSelector, lruMemoize, weakMapMemoize } from 'reselect';
import {
  formatChainsConfigToChains,
  ChainID,
  ChainPath,
} from '@ankr.com/chains-list';

import { MultiService } from 'modules/api/MultiService';
import { RootState } from 'store';
import { chainsFetchChainNodesDetail } from 'modules/chains/actions/fetchChainNodesDetail';
import { deepEqulityCheck } from 'modules/common/utils/deepEqualityCheck';
import { getAddIsArchiveCB } from 'modules/chains/utils/isArchive';
import { getUniqueArray } from 'modules/common/utils/getUniqueArray';

import { clearPathPrefix } from '../utils/clearPathPrefix';
import { fetchBlockchains } from '../actions/fetchBlockchains';
import { filterPathsExceptions } from '../utils/filterPathsExceptions';
import { flatChains } from '../utils/flatChains';
import { getBeaconChains } from '../utils/getBeaconChains';
import { getChainBySubchainID } from '../utils/getChainBySubchainID';
import { getPathsByChainID } from '../utils/getPathsByChainID';
import { getPathsFromChains } from '../utils/getPathsFromChains';
import { getSubchainIds } from '../utils/getSubchainIds';
import { mapSharkChainToChainPath } from '../utils/mapSharkChainNameToChainPath';

const selectBlockchainsState = fetchBlockchains.select();

export const selectNodesDetails = chainsFetchChainNodesDetail.select();

export const selectBlockchains = createSelector(
  selectBlockchainsState,
  ({ data = [] }) => data,
);

const selectNodesDetailsData = createSelector(
  selectNodesDetails,
  ({ data: nodes = [] }) => nodes,
);

export const selectBlockchainsLoadingStatus = createSelector(
  selectBlockchainsState,
  ({ isLoading, isUninitialized }) => isLoading || isUninitialized,
);

export const selectNodesDetailsLoadingStatus = createSelector(
  selectNodesDetails,
  ({ isLoading }) => isLoading,
);

export const selectPublicBlockchains = createSelector(
  selectBlockchains,
  selectNodesDetailsData,
  (blockchains, nodes) => {
    const addIsArchive = getAddIsArchiveCB(nodes);

    const publicChains =
      MultiService.getService().formatPublicEndpoints(blockchains);

    return formatChainsConfigToChains(publicChains).map(addIsArchive);
  },
);

export const selectTotalChainsCount = createSelector(
  selectPublicBlockchains,
  blockchains => {
    return blockchains.length;
  },
);

export const selectBlockchainsCountRounded = createSelector(
  selectPublicBlockchains,
  blockchains => {
    return {
      publicBlockchainsCount:
        Math.floor(
          blockchains.filter(blockchain => !blockchain.premiumOnly).length / 5,
        ) * 5,
      premiumBlockchainsCount: Math.floor(blockchains.length / 5) * 5,
    };
  },
);

export const selectConfiguredBlockchainsForToken = createSelector(
  selectBlockchains,
  selectNodesDetailsData,
  (_state: RootState, userEndpointToken?: string) => userEndpointToken,
  (blockchains, nodes, userEndpointToken) => {
    const addIsArchive = getAddIsArchiveCB(nodes);

    const privateChains = MultiService.getService().formatPrivateEndpoints(
      blockchains,
      userEndpointToken,
    );

    return formatChainsConfigToChains(privateChains).map(addIsArchive);
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectBlockchainBySubchainId = createSelector(
  selectPublicBlockchains,
  (_state: RootState, chainId?: ChainID) => chainId,
  (chains, chainID) => {
    if (!chainID) return;

    return getChainBySubchainID({ chainID, chains });
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectPublicChainById = createSelector(
  (state: RootState, chainId: ChainID) => ({
    state,
    chainId,
  }),
  selectPublicBlockchains,
  ({ chainId, state }, blockchains) => {
    const mainChain = selectBlockchainBySubchainId(state, chainId);

    return blockchains?.find(chain => chain?.id === mainChain?.id);
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
    memoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectPrivateChainById = createSelector(
  (state: RootState, chainId: ChainID, userEndpointToken?: string) => ({
    state,
    chainId,
    userEndpointToken,
  }),
  ({ chainId, state, userEndpointToken }) => {
    const blockchains = selectConfiguredBlockchainsForToken(
      state,
      userEndpointToken,
    );
    const mainChain = selectBlockchainBySubchainId(state, chainId);

    return blockchains?.find(chain => chain?.id === mainChain?.id);
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
    memoizeOptions: { resultEqualityCheck: deepEqulityCheck },
  },
);

export const selectSubChainIdsByChainId = createSelector(
  selectPublicBlockchains,
  (_state: RootState, chainId: ChainID) => chainId,
  (blockchains, chainId) => {
    const currentChain = blockchains?.find(
      blockchain => blockchain.id === chainId,
    );

    if (currentChain) {
      return getSubchainIds(currentChain);
    }

    return [chainId];
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectAllChainsPaths = createSelector(
  selectBlockchains,
  blockchains => getPathsFromChains(blockchains),
);

export const selectAllPathsByChainId = createSelector(
  selectPublicBlockchains,
  (_state: RootState, chainId?: ChainID) => chainId,
  selectAllChainsPaths,
  (chains, chainID, allChainsPaths = []) =>
    getPathsByChainID({
      allChainsPaths,
      chainID,
      chains,
    }),
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectNameByChainPath = createSelector(
  selectBlockchains,
  (_state: RootState, chainPath: ChainPath) => chainPath,
  (chains, chainPath) => {
    const chainByPath = chains.find(
      chain =>
        chain.paths &&
        chain.paths[0] &&
        clearPathPrefix(chain.paths[0]) === chainPath,
    );

    if (!chainByPath) {
      return chainPath;
    }

    return chainByPath.name;
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectAllPathsExceptSubchainsForChainId = createSelector(
  (state: RootState, chainId: ChainID) => ({
    state,
    chainId,
  }),
  selectAllChainsPaths,
  ({ chainId, state }, allChainsPaths) => {
    const currentChainPaths = selectAllPathsByChainId(state, chainId);

    return allChainsPaths.filter(path => !currentChainPaths.includes(path));
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: lruMemoize,
    memoizeOptions: {
      maxSize: 100,
      resultEqualityCheck: deepEqulityCheck,
    },
  },
);

export const selectConfiguredSubChainByPath = createSelector(
  selectPublicBlockchains,
  (_state: RootState, path?: string) => path,
  (blockchains, path) => {
    if (!path) return;

    const subChain = flatChains(blockchains)?.find(blockchain =>
      blockchain.paths
        ?.map(blockchainPath => clearPathPrefix(blockchainPath))
        .includes(path),
    );

    return subChain;
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectSubChainIdByPath = createSelector(
  selectBlockchains,
  (_state: RootState, path?: string) => path,
  (blockchains, path) => {
    if (!path) return;

    const chain = blockchains?.find(blockchain =>
      blockchain.paths
        ?.map(blockchainPath => clearPathPrefix(blockchainPath))
        .includes(path),
    );

    return chain?.id as ChainID | undefined;
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectChainById = createSelector(
  selectPublicBlockchains,
  (_state: RootState, chainId: ChainID) => chainId,
  (blockchains, chainId) => {
    return blockchains?.find(
      blockchain =>
        blockchain.id === chainId || blockchain.paths?.includes(chainId),
    );
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectChainIdsByPaths = createSelector(
  (_state: RootState, selectedChainPaths: ChainPath[]) => selectedChainPaths,
  selectBlockchains,
  (selectedChainPaths, blockchains) => {
    const chainIds = selectedChainPaths.map(selectedChainPath => {
      const chain = blockchains?.find(blockchain =>
        blockchain.paths?.some(
          blockchainPath =>
            clearPathPrefix(blockchainPath) ===
            mapSharkChainToChainPath(selectedChainPath),
        ),
      );

      return chain?.id;
    });

    return getUniqueArray(chainIds.filter(Boolean) as ChainID[]);
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectChainPathsByIds = createSelector(
  (_state: RootState, selectedChainIds: ChainID[]) => selectedChainIds,
  selectBlockchains,
  (selectedChainIds, blockchains) => {
    const chainPaths = selectedChainIds.map(selectedChainId => {
      const chain = blockchains?.find(
        blockchain => blockchain.id === selectedChainId,
      );

      if (!chain || chain?.paths?.length === 0) return selectedChainId;

      const chainPath = chain!.paths![0];

      return clearPathPrefix(chainPath);
    });

    return getUniqueArray(chainPaths.filter(Boolean)).filter(
      filterPathsExceptions,
    );
  },
  {
    argsMemoize: weakMapMemoize,
    memoize: weakMapMemoize,
  },
);

export const selectBeaconChains = createSelector(
  selectPublicBlockchains,
  chains => getBeaconChains(chains),
);
